import { Elements, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAct } from 'FlowTaker/store';
import useLanguage from 'FlowBuilder/hooks/useLanguageFlow';
import { newrelic } from 'lib/newrelic';

const checkoutOptions = {
  emailRequired: true,
  paymentMethods: {
    googlePay: 'auto',
    applePay: 'auto',
    paypal: 'auto'
  },
  buttonType: {
    applePay: 'donate',
    googlePay: 'donate',
    paypal: 'pay'
  },
  layout: {
    overflow: 'never'
  }
};

const paypalCheckoutOptions = {
  phoneNumberRequired: false,
  billingAddressRequired: false,
  shippingAddressRequired: false, // Only supported for one-off payments
  paymentMethods: {
    googlePay: 'never',
    applePay: 'never',
    link: 'never',
    klarna: 'never',
    paypal: 'auto'
  },
  buttonType: {
    paypal: 'paypal'
  },
}

const ExpressButtons = ({
  amountInMinorUnits,
  onComplete,
  isMonthly,
  coverFee,
  currency,
  stripePromise,
  returnUrl,
  paypalButton = false,
  visible = true
}) => {
  // Set up options for the Stripe Elements
  const act = useAct();
  const language = useLanguage();
  const elementsOptions = {
    mode: isMonthly ? 'subscription' : 'payment',
    amount: amountInMinorUnits,
    currency,
    setupFutureUsage: 'off_session',
    locale: language
  };

  if (amountInMinorUnits === 0 || amountInMinorUnits === null) return null;

  const stripePromiseFinal = useMemo(() => {
    if (paypalButton) {
      return act.taker.getPaypalStripePromise(language)
    } else {
      return stripePromise
    }
  }, [])

  return <div style={{ display: visible ? 'block' : 'none' }}>
    <Elements stripe={stripePromiseFinal} options={elementsOptions}>
      <ExpressCheckoutContent
        amountInMinorUnits={amountInMinorUnits}
        onComplete={onComplete}
        isMonthly={isMonthly}
        coverFee={coverFee}
        currency={currency}
        returnUrl={returnUrl}
        expressCheckoutOptions={paypalButton ? paypalCheckoutOptions : checkoutOptions}
      />
    </Elements>
  </div>;
};


const ExpressCheckoutContent = ({
  amountInMinorUnits,
  onComplete,
  isMonthly,
  coverFee,
  currency,
  expressCheckoutOptions
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const act = useAct()
  const stripe = useStripe();
  const elements = useElements();
  const errorRerenderCount = useRef(0);

  // This function handles the checkout flow using your existing controller
  const handleConfirm = async (event) => {
    console.log({ event })
    const billingDetails = event.billingDetails || {};
    const useMainAccount = event.expressPaymentType === 'paypal'

    try {
      setLoading(true);
      setError(null);

      // Use your existing controller endpoint
      const { data } = await act.taker.createStripePaymentIntent({ amountInMinorUnits, isMonthly, coverFee, billingDetails, currency, useMainAccount })
      const { clientSecret, paymentIntentId, subscriptionId } = data

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.origin + '/admin/billing/stripe/example'
        }
      });

      if (confirmError) {
        console.error('Payment failed:', confirmError);
        if (newrelic) newrelic.noticeError(confirmError);
        setError(confirmError.message);
        errorRerenderCount.current += 1;
      } else {
        // Payment succeeded
        onComplete({
          success: true,
          stripePaymentIntentId: paymentIntentId,
          stripeSubscriptionId: subscriptionId,
          useMainAccount
        });
      }
    } catch (error) {
      console.error('Error:', error);
      if (newrelic) newrelic.noticeError(error);
      setError(error.message || 'An unexpected error occurred');
      errorRerenderCount.current += 1;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="checkout-page">
      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
          {error}
        </div>
      )}
      <ExpressCheckoutElement
        key={errorRerenderCount.current} // Force re-mount on error to reset internal state
        onConfirm={handleConfirm}
        options={expressCheckoutOptions}
      />
      {loading && <div className="loading">Processing payment...</div>}
    </div>
  );
};

export default ExpressButtons;
